import { useState } from 'react';
import {
    obtenerCuentasPorEmpleado,
    agregarCuentaAEmpleado,
    actualizarCuentaEmpleado,
    eliminarCuentaDeEmpleado
} from '../../../services/empleado/empleadoDetalleService';

// Helper to extract IDs (consistent across modules)
const extraerId = (dato) => {
    if (!dato) return null;
    if (typeof dato === 'object' && dato.configuracionDetId) return dato.configuracionDetId;
    if (!isNaN(parseInt(dato))) return parseInt(dato);
    return dato;
};

const emptyAccount = { 
    id: null, 
    numeroCuenta: '', 
    bancoId: null,     
    tipoCuentaId: null, 
    esPrincipal: false 
};

export function useCuentasBancarias({ empleadoId, toast }) {

    // 1. STATES
    const [accounts, setAccounts] = useState([]);
    const [accountData, setAccountData] = useState(emptyAccount);
    const [accountDialog, setAccountDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [loading, setLoading] = useState(false);

    // 2. LOAD DATA
    const recargarCuentas = async (empleadoId) => {
        if (!empleadoId) { setAccounts([]); return; }
        try {
            const nuevasCuentas = await obtenerCuentasPorEmpleado(empleadoId);
            const formateadas = nuevasCuentas.map(c => ({
                id: c.cuentaId,
                numeroCuenta: c.numeroCuenta,
                bancoId: extraerId(c.bancoConfigDet) || c.bancoConfigDetId,
                tipoCuentaId: extraerId(c.tipoCuentaConfigDet) || c.tipoCuentaConfigDetId,
                esPrincipal: c.esPrincipal
            })).sort((a, b) => a.id - b.id); // Stable sort by ID

            setAccounts(formateadas);
        } catch (e) {
            console.error("Error loading accounts:", e);
        }
    }

    // 3. MODAL MANAGEMENT
    const openNewAccount = () => {
        setAccountData(emptyAccount);
        setSubmitted(false);
        setAccountDialog(true);
    };

    const openEditAccount = (rowData) => {
        setAccountData({ ...rowData });
        setAccountDialog(true);
    };

    // 4. CRUD: SAVE
    const saveAccount = async () => {
        if (loading) return;
        if (!empleadoId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Guarde el empleado primero' });
            return;
        }

        if (!accountData.bancoId || !accountData.tipoCuentaId || !accountData.numeroCuenta) {
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Complete todos los campos.' 
            });
            return;
        }

        const payload = {
            empleadoId,
            numeroCuenta: accountData.numeroCuenta,
            bancoConfigDetId: accountData.bancoId,
            tipoCuentaConfigDetId: accountData.tipoCuentaId,
            esPrincipal: accountData.esPrincipal || false,
        };

        try {
            setLoading(true);
            if (accountData.id) {
                await actualizarCuentaEmpleado({ ...payload, cuentaId: accountData.id });
                toast.current.show({ severity: 'success', summary: 'Actualizado' });
            } else {
                await agregarCuentaAEmpleado(payload);
                toast.current.show({ severity: 'success', summary: 'Creado' });
            }

            await recargarCuentas(empleadoId);
            setAccountDialog(false);
            setAccountData(emptyAccount);
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error' });
        } finally {
            setLoading(false);
        }

    };

    // 5. CRUD: DELETE
    const deleteAccount = async (rowData) => {
        if (!window.confirm("¿Eliminar cuenta?")) return;
        try {
            await eliminarCuentaDeEmpleado(rowData.id);
            toast.current.show({ severity: 'success', summary: 'Eliminado' });
            await recargarCuentas(empleadoId);
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error' });
        }
    };

    // 6. PRINCIPAL ACCOUNT LOGIC
    const handleSetPrincipalAccount = async (account) => {
        
        if (account.esPrincipal) return;

        // Backup visual optimista
        const originalAccounts = [...accounts];
        
        // Cambio visual inmediato: poner esta en true y el resto en false
        setAccounts(prev => prev.map(c => ({
            ...c,
            esPrincipal: c.id === account.id
        })));

        try {
            const payload = {
                empleadoId,
                cuentaId: account.id,
                numeroCuenta: account.numeroCuenta,
                bancoConfigDetId: account.bancoId,
                tipoCuentaConfigDetId: account.tipoCuentaId,
                esPrincipal: true
            };

            await actualizarCuentaEmpleado(payload);
            toast.current.show({ severity: 'success', summary: 'Actualizado', detail: 'Cuenta principal establecida.' });
            // Confirmamos recargando del server para estar 100% seguros
            await recargarCuentas(empleadoId);

        } catch (error) {
            console.error("Error al cambiar cuenta principal:", error);
            setAccounts(originalAccounts); // Rollback si falla
            toast.current.show({ severity: 'error', summary: 'Error' });
        }
    };

    return {
        accounts,
        accountData,
        setAccountData,
        setAccounts,
        accountDialog,      
        setAccountDialog,  
        recargarCuentas,
        openNewAccount,
        openEditAccount,
        saveAccount,
        deleteAccount,
        handleSetPrincipalAccount,
        loading,
    };
}